<template>
    <div>
        <Dialog 
            v-model="showFeatureSelector" 
            :title="$t('FeatureList.customize')"
            width="40%"
            :hidePadding="true"
            :infoText="$t('FeatureSelector.info')"
        >
            <FeatureSelector />
        </Dialog>
        <Dialog
            v-model="showMissingProperties"
            width="60%"
            :title="$t('FeatureList.missing_properties.title')"
        >
            <span>{{ $t('FeatureList.missing_properties.text') }}</span>
            <JSONTreeview 
                :data="missingProperties"
                :translateData="true"
            />
        </Dialog>
        <Dialog
            v-model="showDeviceSetupEditor"
            :title="$t('FeatureList.setup.change_title')"
            width="400px"
        >
            <div class="text-caption text--secondary mb-4">
                {{ $t('FeatureList.setup.change_hint') }}
            </div>
            <div class="text-caption text--secondary mb-2">
                {{ $t('FeatureList.setup.step_device') }}
            </div>
            <HardwareSelector
                local
                :value="draftHardwareVersion"
                @input="onDraftHardwareChange"
            />
            <div class="text-caption text--secondary mb-2 mt-4">
                {{ $t('FeatureList.setup.step_firmware') }}
            </div>
            <VersionSelector
                local
                :hideInfo="isMobileSize()"
                :value="draftVersions"
                :hardwareVersion="draftHardwareVersion"
                @input="draftVersions = $event"
            />
            <div class="d-flex justify-end mt-6">
                <v-btn
                    text
                    class="mr-2"
                    @click="showDeviceSetupEditor = false"
                >
                    {{ $t('FeatureList.setup.cancel') }}
                </v-btn>
                <v-btn
                    color="primary"
                    :disabled="!canConfirmDeviceSetup"
                    @click="confirmDeviceSetup"
                >
                    {{ $t('FeatureList.setup.apply') }}
                </v-btn>
            </div>
        </Dialog>
        <v-skeleton-loader
            v-if="loading"
            type=  "list-item, list-item-avatar, 
                    list-item, list-item-avatar, list-item-avatar, list-item-avatar, list-item-avatar, list-item-avatar, 
                    list-item, list-item-avatar, list-item-avatar, list-item-avatar, list-item-avatar, list-item-avatar, list-item-avatar, 
                    list-item, list-item-avatar, list-item-avatar"
            elevation="2"
        />
        <v-list
            v-else
            :elevation="isMobileSize() ? 0 : 2"
            :style="`max-height: 100vh - 16px`"
            class="overflow-y-auto py-0"
        >
            <v-list-item
                class="device-setup-item"
                @click="showDeviceSetupEditor = true"
            >
                <v-list-item-icon>
                    <v-icon color="primary">mdi-cellphone-cog</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title class="primary--text text-wrap" style="word-break: break-word">
                        {{ hardwareLabel }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-wrap" style="word-break: break-word">
                        {{ firmwareLabel }}
                    </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                    <v-icon small color="primary">mdi-pencil</v-icon>
                </v-list-item-action>
            </v-list-item>
            <v-divider />

            <div v-if="isLoggedIn">
                <v-list-item @click="showFeatureSelector = true">
                    <v-list-item-icon>
                        <v-icon>mdi-playlist-edit</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title class="text-wrap" style="word-break: break-word">{{ $t("FeatureList.customize") }}</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
                <v-divider/>
            </div>

            <div v-if="$route.name != 'store-editor'">
                <v-list-item 
                    @click="showTemplateFeaturesEditor = !showTemplateFeaturesEditor"
                >
                    <v-list-item-icon>
                        <v-icon>mdi-file-document-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title class="text-wrap" style="word-break: break-word">Configuration template</v-list-item-title>
                    </v-list-item-content> 
                </v-list-item>
                <v-divider/>
            </div>

            <v-expansion-panels 
                v-model="expand"
                flat 
            >
                    <v-expansion-panel>
                        <v-expansion-panel-header v-if="template">All features</v-expansion-panel-header>
                        <v-expansion-panel-content class="px-0">
                            <v-list-item-group
                                color="primary"
                                class="px-0"
                            >
                                <div v-for="category, categoryName in categories" :key="categoryName">
                                    <v-subheader 
                                        v-if="Object.keys(featuresInCategory(categoryName)).length > 0"
                                        class='text-wrap' 
                                        style="word-break: break-word"
                                    >
                                        {{ translate(category.title) }}
                                    </v-subheader>
                                    <div v-for="feature, featureName in featuresInCategory(categoryName)" :key="featureName">
                                        <v-list-item 
                                            v-if="!featureMinVersionRequirementsMet(feature.min_version)"
                                            disabled
                                        >
                                            <v-list-item-icon>
                                                <v-icon>
                                                    {{ feature.icon }}
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content>
                                                <v-list-item-title class="text-wrap" style="word-break: break-word">
                                                    {{ translate(feature.title) }}
                                                </v-list-item-title>
                                                <v-list-item-subtitle class="text-wrap" style="word-break: break-word">
                                                    {{ $t('FeatureList.version_too_low')}}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-list-item 
                                            v-else-if="!featureMaxVersionRequirementsMet(feature.max_version)"
                                            disabled
                                        >
                                            <v-list-item-icon>
                                                <v-icon>
                                                    {{ feature.icon }}
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content>
                                                <v-list-item-title class="text-wrap" style="word-break: break-word">
                                                    {{ translate(feature.title) }}
                                                </v-list-item-title>
                                                <v-list-item-subtitle class="text-wrap" style="word-break: break-word">
                                                    {{ $t('FeatureList.version_too_high') }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-list-item
                                            v-else-if="featureIsUndefined(featureName)"
                                            disabled
                                        >
                                            <v-list-item-icon>
                                                <v-icon>
                                                    {{ feature.icon }}
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content>
                                                <v-list-item-title class="text-wrap" style="word-break: break-word">
                                                    {{ translate(feature.title) }}
                                                </v-list-item-title>
                                                <v-list-item-subtitle class="text-wrap" style="word-break: break-word">
                                                    {{ $t('FeatureList.feature_is_undefined') }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-list-item 
                                            v-else-if="feature.byte_groups"
                                            @click="selected = {featureName}"
                                        >
                                            <v-list-item-icon>
                                                <v-badge
                                                    icon="mdi-pencil"
                                                    :value="featureIsDirty(featureName)"
                                                    color='warning'
                                                >
                                                    <v-icon :color="featureIsValid(featureName) ? null : 'red'">
                                                        {{ feature.icon }}
                                                    </v-icon>
                                                </v-badge>
                                            </v-list-item-icon>
                                            <v-list-item-content>
                                                <v-list-item-title :class="featureIsValid(featureName) ? 'text-wrap' : 'text-wrap font-weight-bold red--text'" style="word-break: break-word">
                                                    {{ translate(feature.title) }}
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-list-group v-else-if="hasVisiblePropertyGroups(feature)">
                                            <template v-slot:prependIcon>
                                                <v-badge
                                                    icon="mdi-pencil"
                                                    :value="featureIsDirty(featureName)"
                                                    color='warning'
                                                >
                                                    <v-icon :color="featureIsValid(featureName) ? null : 'red'">
                                                        {{ feature.icon }}
                                                    </v-icon>
                                                </v-badge>
                                            </template>
                                            <template v-slot:activator>
                                                <v-list-item-content>
                                                    <v-list-item-title :class="featureIsValid(featureName) ? 'text-wrap' : 'text-wrap font-weight-bold red--text'" style="word-break: break-word">
                                                        {{ translate(feature.title) }}
                                                    </v-list-item-title>
                                                </v-list-item-content>
                                            </template>
                                            <v-list-item 
                                                v-for="propertyGroup, propertyGroupName in visiblePropertyGroupsFor(feature)" 
                                                :key="propertyGroupName"
                                                @click="selected = {featureName, propertyGroupName}"
                                            >
                                                <v-list-item-content>
                                                    <v-badge
                                                        :value="propertyGroupIsDirty(featureName, propertyGroupName)"
                                                        color='warning'
                                                        dot
                                                        inline
                                                    >    
                                                        <v-list-item-icon/>
                                                        <v-list-item-title :class="propertyGroupIsValid(featureName, propertyGroupName) ? 'text-wrap' : 'text-wrap red--text'" style="word-break: break-word">
                                                            {{ translate(propertyGroup.title) }}
                                                        </v-list-item-title>
                                                    </v-badge>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list-group>
                                    </div>
                                </div>
                            </v-list-item-group>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
        </v-list>
    </div>
</template>

<script>
import Dialog from '@/components/dialogs/Dialog.vue'
import FeatureSelector from '@/components/FeatureSelector.vue'
import JSONTreeview from '@/components/JSONTreeview.vue'
import VersionSelector from '@/components/VersionSelector.vue'
import HardwareSelector from '@/components/HardwareSelector.vue'

import stMinVersionMet from '@/utils/stMinVersionMet.js';
import stMaxVersionMet from '@/utils/stMaxVersionMet.js';
import propertyGroupVisible, { visiblePropertyGroups } from '@/utils/propertyGroupVisible.js';
import formatHardwareLabel from '@/utils/formatHardwareLabel.js';

import { getProperty } from 'dot-prop';

export default {
    components: {
        Dialog,
        FeatureSelector,
        JSONTreeview,
        VersionSelector,
        HardwareSelector
    },
    data: () => ({
        showFeatureSelector: false,
        showMissingProperties: false,
        showDeviceSetupEditor: false,
        draftHardwareVersion: null,
        draftVersions: null,
        expand: 0
    }),
    computed: {
        loading() {
            return this.$store.getters.loadingEditor
        },
        categories() {
            return this.$store.getters.categories
        },
        features() {
            return this.$store.getters.features
        },
        selected: {
            get() {
                return this.$store.getters.selected
            },
            set(newValue) {
                this.$store.dispatch("setSelected", newValue)
            }
        },
        versions() {
            return this.$store.getters.versions
        },
        difficulty() {
            return this.$store.getters.difficulty
        },
        featureNamesInDesired() {
            return this.$store.getters.featureNamesInDesired
        },
        missingProperties() {
            return this.$store.getters.missingProperties
        },
        isLoggedIn() {
            return this.$store.getters.isLoggedIn
        },
        isAdmin() {
            return this.$store.getters.isAdmin
        },
        template() {
            return this.$store.getters.template
        },
        propertyGroupVisibilityOptions() {
            return {
                stVersion: this.versions?.st,
                isAdmin: this.isAdmin
            }
        },
        hardwareLabel() {
            const name = this.$store.getters.selectedHardwareName
            if (!name) {
                return this.$t('FeatureList.setup.step_device')
            }
            return formatHardwareLabel(name)
        },
        firmwareLabel() {
            const st = this.versions?.st
            if (!st) {
                return this.$t('FeatureList.setup.step_firmware')
            }
            return `ST ${st}`
        },
        canConfirmDeviceSetup() {
            return this.draftHardwareVersion != null && this.draftVersions != null
        },
        showTemplateFeaturesEditor: {
            get() {
                return this.$store.getters.showTemplateFeaturesEditor
            },
            set(show) {
                this.$store.dispatch('showTemplateFeaturesEditor', show)
            }
        }
    },
    methods: {
        onDraftHardwareChange(value) {
            this.draftHardwareVersion = value
            this.draftVersions = null
        },
        confirmDeviceSetup() {
            if (!this.canConfirmDeviceSetup) {
                return
            }
            const currentHardware = this.$store.getters.selectedHardwareVersion
            if (this.draftHardwareVersion !== currentHardware) {
                this.$store.dispatch('setSelectedHardwareVersion', this.draftHardwareVersion)
            }
            this.$store.dispatch('setVersions', this.draftVersions)
            this.showDeviceSetupEditor = false
        },
        featuresInCategory(category) {
            let featuresInCategory = {}
            Object.keys(this.features).forEach(featureName => {
                let feature = this.features[featureName]
                if(feature.category == category && this.$store.getters.featureSupportedByHardware(featureName)) {
                    featuresInCategory[featureName] = feature
                }
            })
            return featuresInCategory
        },
        featureIsDirty(featureName) {
            return this.$store.getters.dirty(featureName)
        },
        featureIsValid(featureName) {
            return this.$store.getters.valid(featureName)
        },
        featureIsUndefined(featureName) {
            return this.featureNamesInDesired.indexOf(featureName) == -1
        },
        propertyGroupIsDirty(featureName, propertyGroupName) {
            return this.$store.getters.dirty(featureName, propertyGroupName)
        },
        propertyGroupIsValid(featureName, propertyGroupName) {
            return this.$store.getters.valid(featureName, propertyGroupName)
        },
        featureMinVersionRequirementsMet(featureMinVersions) {
            let stMin = featureMinVersions.st
            return stMinVersionMet(this.versions.st, stMin);
        },
        featureMaxVersionRequirementsMet(featureMaxVersions) {
            let stMax = getProperty(featureMaxVersions, 'st')
            return stMaxVersionMet(this.versions.st, stMax);
        },
        visiblePropertyGroupsFor(feature) {
            return visiblePropertyGroups(feature.property_groups, this.propertyGroupVisibilityOptions)
        },
        hasVisiblePropertyGroups(feature) {
            return Object.keys(this.visiblePropertyGroupsFor(feature)).length > 0
        },
        clearHiddenPropertyGroupSelection() {
            const selected = this.selected
            if (!selected?.propertyGroupName) {
                return
            }
            const feature = this.features?.[selected.featureName]
            const group = feature?.property_groups?.[selected.propertyGroupName]
            if (!propertyGroupVisible(group, this.propertyGroupVisibilityOptions)) {
                this.selected = null
            }
        }
    },
    watch: {
        missingProperties(properties) {
            if(properties && Object.keys(properties).length > 0) {
                this.showMissingProperties = true
            }
        },
        template(template) {
            if(template) { 
                this.expand = null
            }
            else { 
                this.expand = 0
            }
        },
        selected(val) {
            if(val) {
                this.showTemplateFeaturesEditor = false
            }
            this.clearHiddenPropertyGroupSelection()
        },
        versions: {
            handler() {
                this.clearHiddenPropertyGroupSelection()
            },
            deep: true
        },
        isAdmin() {
            this.clearHiddenPropertyGroupSelection()
        },
        showDeviceSetupEditor(open) {
            if (open) {
                this.draftHardwareVersion = this.$store.getters.selectedHardwareVersion
                this.draftVersions = this.$store.getters.versions
            }
        },
        showTemplateFeaturesEditor(val) {
            if(val) {
                this.selected = null
            }
        }
    }
}
</script>

<style scoped>
.v-expansion-panel-content>>> .v-expansion-panel-content__wrap {
    padding: 0 !important;
}
.device-setup-item {
    background-color: rgba(88, 103, 221, 0.08);
}
.device-setup-item:hover {
    background-color: rgba(88, 103, 221, 0.14) !important;
}
.theme--dark .device-setup-item {
    background-color: rgba(88, 103, 221, 0.16);
}
.theme--dark .device-setup-item:hover {
    background-color: rgba(88, 103, 221, 0.24) !important;
}
</style>
